import { z } from 'zod';
// Owner kicks off a rotation: commits to a fresh K_album, supplies the
// wrappedAlbumKey for themselves and one entry per recipient they want to
// keep. Recipients not listed here are revoked when rotate-commit runs.
export const RotateInitRequest = z.object({
    wrappedAlbumKeyForOwner: z.string().min(1),
    recipientWraps: z
        .array(z.object({
        recipientUserId: z.string().min(1),
        wrappedAlbumKey: z.string().min(1),
    }))
        .max(100),
});
export const RotateInitResponse = z.object({
    rootAlbumId: z.string(),
    // The next-generation number. Photos rotated under this gen will carry
    // keyGen = nextKeyGen; the album's own keyGen flips to this value at commit.
    nextKeyGen: z.number().int().positive(),
});
// Mirror of UploadBatchItem but on an existing photo: the s3Key is reused
// (the blob is replaced in-place with the re-encrypted container).
export const RotatePhotosBatchItem = z.object({
    albumId: z.string().min(1),
    photoId: z.string().min(1),
    partsHint: z.number().int().positive(),
    // New encryptedMeta encrypted under K_album_new. Persisted alongside the
    // multipart upload id so rotate-photos/complete can swap it in atomically.
    encryptedMeta: z.string().min(1),
    // Optional new encryptedRating under K_album_new — only sent for photos that
    // have a rating today. Backend persists it as encryptedRatingNext; commit
    // swaps it with encryptedRating atomically when the multipart completes.
    encryptedRating: z.string().min(1).optional(),
    // Size in bytes of the re-encrypted container. Bookkeeping only.
    sizeBytes: z.number().int().positive(),
});
export const RotatePhotosBatchRequest = z.object({
    items: z.array(RotatePhotosBatchItem).min(1).max(100),
});
export const RotatePhotosBatchResultItem = z.object({
    photoId: z.string(),
    uploadId: z.string(),
    partUrls: z.array(z.string().url()),
});
export const RotatePhotosBatchResponse = z.object({
    items: z.array(RotatePhotosBatchResultItem),
});
export const RotatePhotosCompleteItem = z.object({
    albumId: z.string().min(1),
    photoId: z.string().min(1),
    uploadId: z.string().min(1),
    parts: z
        .array(z.object({
        partNumber: z.number().int().positive(),
        etag: z.string().min(1),
    }))
        .min(1),
});
export const RotatePhotosCompleteRequest = z.object({
    items: z.array(RotatePhotosCompleteItem).min(1).max(100),
});
// Album names are encrypted under K_album, so a key rotation invalidates
// every encryptedName under the root. The client must re-encrypt each one
// with K_album_new and submit the full set at commit time so the backend
// can swap names atomically with the album wrappedAlbumKey swap.
export const RotateCommitRequest = z.object({
    albumNames: z
        .array(z.object({
        albumId: z.string().min(1),
        encryptedName: z.string().min(1),
    }))
        .max(1000),
});
export const RotateCommitResponse = z.object({
    rootAlbumId: z.string(),
    newKeyGen: z.number().int().positive(),
    sharesKept: z.number().int().nonnegative(),
    sharesRevoked: z.number().int().nonnegative(),
});
// Photos under the root that are still on the old keyGen. Used by the client
// orchestrator to discover what's left, both on a fresh rotation start and on
// resume (after a tab refresh mid-rotation, the album keeps its
// wrappedAlbumKeyNext and this list shrinks as photos get rotated).
export const RotationPendingResponse = z.object({
    rootAlbumId: z.string(),
    oldKeyGen: z.number().int().positive(),
    newKeyGen: z.number().int().positive(),
    // Owner's wrappedAlbumKeyNext (= K_album_new wrapped for the owner). Returned
    // here so the client can unwrap it on resume without a second round-trip.
    wrappedAlbumKeyNext: z.string(),
    photos: z.array(z.object({
        photoId: z.string(),
        albumId: z.string(),
        s3Key: z.string(),
        // The pre-rotation encryptedMeta — needed to recover the plaintext
        // filename/mime so the new container can be re-stamped under K_new.
        encryptedMeta: z.string(),
        // The pre-rotation encryptedRating, if any. The client decrypts it with
        // K_old, re-encrypts with K_new, and submits it back in /photos-batch.
        encryptedRating: z.string().optional(),
    })),
});
//# sourceMappingURL=rotation.js.map